import React, { Fragment, useCallback, useState } from 'react';
import { clsx } from 'clsx';
import { AddDraftTestModal } from '../../tests/draft/AddDraftTestModal';
import {
  getListGroupLabel,
  getMatchIcon,
  getMatchLabel,
  getScreeningFlowDisplayId,
  getSegmentIcon,
  ObjectSchemaFieldDataTag,
} from './helper';
import {
  MenuItem,
  SalvArrow,
  SalvDataTag,
  SalvIconButton,
  SalvLabel,
  SalvMenuButton,
  SalvProgressCircle,
  SalvTooltipWrapper,
  Size,
} from '@salvapp/ui-components';
import { anchorType } from '@salvapp/ui-components/build/SalvArrow/src';
import {
  InternalApiEntityGroup,
  InternalApiScreeningFlow,
  InternalApiScreeningFlowDraft,
  InternalApiScreeningListType,
} from '../../../api-internal';
import { useNavigate, useSearchParams } from 'react-router';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { useDeleteScreeningFlow } from './screeningFlowActions';
import {
  useActivateFlowDraft,
  useCreateDraftForFlow,
  useDeleteFlowDraft,
} from '../../fields/draft/screeningFlowDraftActions';
import { showConfirmationModal } from '../../../confirmation-modal/confirmationModalReducer';
import { useDispatch } from 'react-redux';
import Has from '../../../auth/Has';
import { CAN_MODIFY_SCREENABLE_FIELDS_CONFIGURATION } from '../../../auth/permissions';
import { faCopy, faFlask, faFlaskVial, faPenToSquare, faRocket, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { showFeedback } from '../../../feedback/feedbackReducer';
import { copyToClipboard } from '../../../common/clipboard';
import {
  getScreeningFlowDraftTestRunsPath,
  getScreeningFlowPath,
  InternalNavigation,
} from '../../../navigation/helper';
import useCustomLists from '../../../custom-lists/useCustomLists';
import { DowJonesCustomListCode } from '../ScreeningDataSetup';
import { useQueryScreeningSelectorsGroups } from '../lists/useQueryScreeningSelectors';
import DataLoadingErrorFallback from '../../../common/errors/DataLoadingErrorFallback';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDate } from '../../../common/datetime/FormattedDateTime';

const EDIT_ACTION = 'EDIT';
const EDIT_DRAFT_ACTION = 'EDIT_DRAFT';
const DELETE_ACTION = 'DELETE';
const RUN_TEST_ACTION = 'RUN_TEST';
const TEST_RUNS_ACTION = 'TEST_RUNS';
const ACTIVATE_ACTION = 'ACTIVATE';
const DISCARD_ACTION = 'DISCARD';
const DUPLICATE_ACTION = 'DUPLICATE';

export type FlowStatus = 'active' | 'draft';

const STATUS_LABEL: Record<FlowStatus, { label: string; variant: 'success' | 'secondary' }> = {
  active: { label: 'Active', variant: 'success' },
  draft: { label: 'Draft', variant: 'secondary' },
};

interface Props {
  rowNr: number;
  flow: InternalApiScreeningFlow;
  segments: InternalApiEntityGroup[];
  // TODO remove?
  status?: FlowStatus;
  showButtonsAndId?: boolean;
  isFlowDraftsEnabled?: boolean;
  pendingDraftCount?: number;
  draft?: InternalApiScreeningFlowDraft;
}

export const Flow = ({
  rowNr,
  flow,
  segments,
  status,
  showButtonsAndId,
  isFlowDraftsEnabled,
  pendingDraftCount,
  draft,
}: Props) => {
  const isDraftRow = status === 'draft';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [showTestModal, setShowTestModal] = useState(false);

  const { mutateAsync: deleteFlow } = useDeleteScreeningFlow(flow.entityType);
  const { mutateAsync: deleteFlowDraft } = useDeleteFlowDraft();
  const { mutateAsync: activateFlowDraft } = useActivateFlowDraft();
  const { mutateAsync: createDraftForFlow } = useCreateDraftForFlow(flow.id!);

  const discardDraftWithConfirmation = useCallback(
    (draftToDiscard: InternalApiScreeningFlowDraft) => {
      dispatch(
        showConfirmationModal({
          title: 'Discard draft?',
          description: 'This permanently deletes the draft and any tests run against it. This cannot be undone.',
          confirmationText: 'Discard',
          confirmButtonVariant: 'danger',
          cancelText: 'Cancel',
          onConfirm: async () => {
            await deleteFlowDraft(draftToDiscard);
          },
        })
      );
    },
    [dispatch, deleteFlowDraft]
  );

  const activateDraftWithConfirmation = useCallback(
    (draft: InternalApiScreeningFlowDraft) => {
      dispatch(
        showConfirmationModal({
          title: 'Activate draft?',
          description:
            "This replaces the live screening flow with this draft's configuration. Changes to screening " +
            "configuration can have a serious impact on your company's compliance operations.",
          confirmationText: 'Activate',
          confirmButtonVariant: 'primary',
          cancelText: 'Cancel',
          onConfirm: async () => {
            await activateFlowDraft(draft);
            dispatch(showFeedback({ message: 'Draft activated', success: true }));
          },
        })
      );
    },
    [dispatch, activateFlowDraft]
  );

  const deleteFlowWithConfirmation = useCallback(
    async (flowId: string) => {
      dispatch(
        showConfirmationModal({
          title: 'Are you sure?',
          description:
            "Any changes to Screening configuration can have a serious impact on your company's compliance operations.",
          confirmationText: 'Yes',
          confirmButtonVariant: 'danger',
          cancelText: 'No',
          onConfirm: async () => {
            await deleteFlow(flowId);
          },
        })
      );
    },
    [dispatch, deleteFlow]
  );

  const handleClick = async (action: string) => {
    if (action === EDIT_ACTION) {
      navigate(
        {
          pathname: InternalNavigation.screeningFlowWizard,
          search: searchParams.toString(),
        },
        {
          state: { flow },
        }
      );
    } else if (action === EDIT_DRAFT_ACTION && draft) {
      navigate(
        {
          pathname: InternalNavigation.screeningFlowWizard,
          search: searchParams.toString(),
        },
        {
          state: { flow: draft.snapshot, isDraft: true, draftId: draft.id, note: draft.note },
        }
      );
    } else if (action === DELETE_ACTION) {
      await deleteFlowWithConfirmation(flow.id!);
    } else if (action === RUN_TEST_ACTION) {
      setShowTestModal(true);
    } else if (action === TEST_RUNS_ACTION && draft) {
      navigate(getScreeningFlowDraftTestRunsPath(flow.id!, draft.id));
    } else if (action === ACTIVATE_ACTION && draft) {
      activateDraftWithConfirmation(draft);
    } else if (action === DISCARD_ACTION && draft) {
      discardDraftWithConfirmation(draft);
    } else if (action === DUPLICATE_ACTION) {
      try {
        await createDraftForFlow({ entityType: flow.entityType, snapshot: flow });
        dispatch(showFeedback({ message: 'Draft created', success: true }));
        navigate(getScreeningFlowPath(flow.id!));
      } catch {
        dispatch(showFeedback({ message: 'Failed to create draft', success: false }));
      }
    }
  };

  const customLists = useCustomLists();
  const dowJonesListId = customLists.find((list) => list.code === DowJonesCustomListCode.DOW_JONES_SANCTION)?.id;
  const {
    data: screeningSelectorsGroups,
    isPending: isScreeningSelectorGroupsPending,
    isFetching: isScreeningSelectorGroupsFetching,
    isError: isScreeningSelectorGroupsError,
  } = useQueryScreeningSelectorsGroups(dowJonesListId);

  if (isScreeningSelectorGroupsError) {
    return <DataLoadingErrorFallback message={'Could not load List Groups'} />;
  }

  const startAnchor: anchorType = {
    position: 'right',
    offset: {
      x: 4,
    },
  };
  const endAnchor: anchorType = {
    position: 'left',
    offset: {
      x: -4,
    },
  };

  const screenableFieldCellId = `screenable-field-${rowNr}`;
  const segmentsCellId = `entity-groups-${rowNr}`;
  const matchTypeCellId = `match-type-${rowNr}`;
  const screenedAgainstCellIds = flow.lists!.map(
    ({ list, usableField }) => `screened-against-${rowNr}-${list.name}` + (usableField ? `-${usableField.name}` : '')
  );

  const includedSegmentsLength = (flow.includedSegments ?? []).length;
  const segmentCount =
    includedSegmentsLength + (flow.excludedSegments ?? []).length + (includedSegmentsLength == 0 ? 1 : 0);

  const screeningFields = flow.field.split(' ');

  const copyFlowId = async (event?: React.MouseEvent) => {
    event?.stopPropagation();
    const copied = await copyToClipboard(flow.id!);
    dispatch(
      showFeedback(
        copied ? { message: 'Value copied', success: true } : { message: 'Could not copy to clipboard', success: false }
      )
    );
  };

  return (
    <div
      className={clsx('flow-row', isDraftRow && 'flow-row--draft', isFlowDraftsEnabled && 'flow-row--clickable')}
      onClick={isFlowDraftsEnabled ? () => navigate(getScreeningFlowPath(flow.id!)) : undefined}
    >
      <div className='screening-field-col' aria-label={`Screenable Field for Flow ${rowNr}`}>
        <div
          id={screenableFieldCellId}
          className={clsx('screenable-field', screeningFields.length > 1 && 'grouping-box')}
        >
          {screeningFields.map((fieldPart) => (
            <Fragment key={fieldPart}>
              {fieldPart.includes('.') ? (
                <ObjectSchemaFieldDataTag key={fieldPart} value={fieldPart} />
              ) : (
                <SalvDataTag key={fieldPart} value={fieldPart} />
              )}
            </Fragment>
          ))}
        </div>
        <SalvArrow start={screenableFieldCellId} end={segmentsCellId} startAnchor={startAnchor} endAnchor={endAnchor} />
        <div className='flow-tags'>
          {status && <SalvLabel variant={STATUS_LABEL[status].variant}>{STATUS_LABEL[status].label}</SalvLabel>}
          {!isDraftRow && pendingDraftCount !== undefined && pendingDraftCount > 0 && (
            <SalvLabel variant='primary' className='pending-draft-count'>
              {pendingDraftCount} {pendingDraftCount === 1 ? 'draft' : 'drafts'}
            </SalvLabel>
          )}
          {isDraftRow && (draft?.testCount ?? 0) > 0 && (
            <SalvLabel variant='secondary'>
              {draft!.testCount} {draft!.testCount === 1 ? 'test' : 'tests'}
            </SalvLabel>
          )}
          {flow.goodList && Object.values(flow.goodList).some((value) => !!value) && (
            <SalvTooltipWrapper
              placement={'right top'}
              tooltip={
                <div className='p-a-4'>
                  <span>Fields used for Goodlisting: </span>
                  {Object.values(flow.goodList)
                    .filter((it) => !!it)
                    .map((goodListKey) => (
                      <span className='goodlist-key' key={goodListKey}>
                        {goodListKey} &nbsp;
                      </span>
                    ))}
                </div>
              }
            >
              <SalvLabel variant='warning' className='goodlist-label'>
                Goodlisting
              </SalvLabel>
            </SalvTooltipWrapper>
          )}
          {
            // TODO dont show label in group view?
            showButtonsAndId && (
              <div className='flow-id text-xs' onClick={copyFlowId}>
                ID: {getScreeningFlowDisplayId(flow.id!)}
                <SalvIconButton
                  ariaLabel={`Copy ID value`}
                  icon={faCopy}
                  className={'copy-button'}
                  onClick={() => copyFlowId()}
                />
              </div>
            )
          }
        </div>
      </div>
      <div aria-label={`Segments for Flow ${rowNr}`}>
        <div id={segmentsCellId} className={clsx('entity-groups', segmentCount > 1 && 'grouping-box')}>
          {flow.includedSegments?.length == 0 && (
            <SalvDataTag icon={getSegmentIcon('included')} label='All Entities'></SalvDataTag>
          )}
          {flow.includedSegments?.map((segmentCode) => (
            <SalvDataTag
              key={segmentCode}
              icon={getSegmentIcon('included')}
              label={segments?.find((segment) => segment.code === segmentCode)?.name || segmentCode}
            />
          ))}
          {flow.excludedSegments?.map((segmentCode) => (
            <SalvDataTag
              key={segmentCode}
              icon={getSegmentIcon('excluded')}
              label={segments?.find((segment) => segment.code === segmentCode)?.name || segmentCode}
            />
          ))}
        </div>
        <SalvArrow start={segmentsCellId} end={matchTypeCellId} startAnchor={startAnchor} endAnchor={endAnchor} />
      </div>
      <div aria-label={`Matching Type for Flow ${rowNr}`}>
        <div className={'match-type'} id={matchTypeCellId}>
          <SalvDataTag
            icon={getMatchIcon(flow.match.type)}
            label={getMatchLabel(flow.match.type)}
            value={flow.match.value}
          />
        </div>
        {screenedAgainstCellIds.map((screenedAgainstCellId) => {
          return (
            <SalvArrow
              key={screenedAgainstCellId}
              start={matchTypeCellId}
              end={screenedAgainstCellId}
              startAnchor={startAnchor}
              endAnchor={endAnchor}
            />
          );
        })}
      </div>
      <div aria-label={`Screened Against for Flow ${rowNr}`}>
        <div className='screened-against-wrapper'>
          <div className={'screened-against'}>
            {flow.lists!.map(({ type, list, usableField, selectorGroupCodes }, i) =>
              type === InternalApiScreeningListType.SANCTION ? (
                <div key={list + (list.id ?? '') + i} className={'list-groups'}>
                  <span className={'list-name'}>
                    <div>{list.name}</div>
                    <SalvTooltipWrapper tooltip={'List Groups that are used for this Flow'}>
                      <FontAwesomeIcon icon={faCircleInfo} />
                    </SalvTooltipWrapper>
                  </span>
                  <div className={'grouping-box'} id={screenedAgainstCellIds[i]}>
                    {isScreeningSelectorGroupsPending ? (
                      <SalvProgressCircle ariaLabel={'Loading List Groups'} />
                    ) : (
                      selectorGroupCodes?.map((groupCode) => {
                        const selectorGroup = screeningSelectorsGroups.find((group) => group.code === groupCode);
                        const label = selectorGroup
                          ? getListGroupLabel(selectorGroup)
                          : isScreeningSelectorGroupsFetching
                            ? 'Loading List Group'
                            : 'Error: List Group cannot be found';

                        return <SalvDataTag key={groupCode} label={label} value={usableField?.name} />;
                      })
                    )}
                  </div>
                </div>
              ) : (
                <div key={list + (list.id ?? '') + i} id={screenedAgainstCellIds[i]}>
                  <SalvDataTag label={list.name} value={usableField?.name} />
                </div>
              )
            )}
          </div>
          {showButtonsAndId && !isFlowDraftsEnabled && (
            <Has allPermissions={[CAN_MODIFY_SCREENABLE_FIELDS_CONFIGURATION]}>
              <div className='button-wrapper' onClick={(event) => event.stopPropagation()}>
                <SalvMenuButton
                  ariaLabel='options'
                  size={Size.SMALL}
                  onAction={([action]) => handleClick(action)}
                  placement='bottom right'
                >
                  {isDraftRow
                    ? [
                        <MenuItem
                          key='edit-draft'
                          title='Edit'
                          value={EDIT_DRAFT_ACTION}
                          icon={faPenToSquare}
                          disabled={!!draft?.testCount}
                        />,
                        <MenuItem key='run-test' title='Run test' value={RUN_TEST_ACTION} icon={faFlask} />,
                        <MenuItem
                          key='test-runs'
                          title='Test runs'
                          value={TEST_RUNS_ACTION}
                          icon={faFlaskVial}
                          disabled={!draft?.testCount}
                        />,
                        <MenuItem key='activate' title='Activate' value={ACTIVATE_ACTION} icon={faRocket} />,
                        <MenuItem
                          key='discard'
                          title='Discard draft'
                          value={DISCARD_ACTION}
                          icon={faTrash}
                          dangerTextColor
                        />,
                      ]
                    : [
                        <MenuItem key='edit' title='Edit' value={EDIT_ACTION} icon={faPenToSquare} />,
                        <MenuItem
                          key='delete'
                          title='Delete'
                          value={DELETE_ACTION}
                          icon={faTrash}
                          dangerTextColor={true}
                        />,
                      ]}
                </SalvMenuButton>
              </div>
            </Has>
          )}
          {showButtonsAndId && isFlowDraftsEnabled && !isDraftRow && (
            <Has allPermissions={[CAN_MODIFY_SCREENABLE_FIELDS_CONFIGURATION]}>
              <div className='button-wrapper' onClick={(event) => event.stopPropagation()}>
                <SalvMenuButton
                  ariaLabel='options'
                  size={Size.SMALL}
                  onAction={([action]) => handleClick(action)}
                  placement='bottom right'
                >
                  <MenuItem key='edit' title='Edit' value={EDIT_ACTION} icon={faPenToSquare} />
                  <MenuItem key='duplicate' title='Duplicate as draft' value={DUPLICATE_ACTION} icon={faCopy} />
                  <MenuItem key='delete' title='Delete' value={DELETE_ACTION} icon={faTrash} dangerTextColor={true} />
                </SalvMenuButton>
              </div>
            </Has>
          )}
        </div>
      </div>
      {draft && (
        <div className='draft-provenance' aria-label={`Draft ${rowNr} provenance`}>
          <span>
            Created by <strong>{draft.createdBy.email}</strong> · {formatDate(draft.createdTime, true)}
          </span>
          {draft.note && <span className='draft-note'>{draft.note}</span>}
        </div>
      )}
      {showTestModal && draft && <AddDraftTestModal draft={draft} onClose={() => setShowTestModal(false)} />}
    </div>
  );
};
